import React, { useEffect, useState } from 'react';
import { Avatar } from '@mui/material';
import axios from '../axiosConfig';
import { resolveApiAssetUrl } from '../lib/resolveApiAssetUrl';

export interface AvatarImageProps {
  name?: string | null;
  filename?: string | null;
  url?: string | null; // optional pre-resolved URL to avoid fetching
  size?: number; // pixels
}

export default function AvatarImage(props: AvatarImageProps) {
  const { name, filename, url, size = 28 } = props;
  const [fetchedUrl, setFetchedUrl] = useState<string | null>(null);
  const resolvedUrlProp = resolveApiAssetUrl(url);
  const resolvedFilenameUrl = resolveApiAssetUrl(filename);

  // Clear any previously fetched blob when the source changes.
  useEffect(() => {
    setFetchedUrl((previousUrl) => {
      if (previousUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previousUrl);
      }
      return null;
    });
  }, [filename, url]);

  // Fetch image from server only when the value is a storage filename.
  useEffect(() => {
    if (!filename || resolvedUrlProp || resolvedFilenameUrl) {
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    const fetchImage = async () => {
      try {
        const resp = await axios.get(`/Image/${encodeURIComponent(filename)}`, { responseType: 'blob' });
        objectUrl = URL.createObjectURL(resp.data);
        if (!cancelled) {
          setFetchedUrl(objectUrl);
        }
      } catch (error) {
        console.error('Failed to fetch avatar image:', error);
      }
    };

    fetchImage();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [filename, resolvedFilenameUrl, resolvedUrlProp]);

  const resolvedUrl =
    resolvedUrlProp ||
    fetchedUrl ||
    resolvedFilenameUrl;

  const initials = (name || 'U')
    .split(/\s+/)
    .map(p => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Heuristic font size for initials
  const fontSize = Math.max(10, Math.round(size * 0.43));

  return (
    <Avatar src={resolvedUrl || undefined} sx={{ width: size, height: size, fontSize }}>
      {initials}
    </Avatar>
  );
}

